import React, { useState } from 'react'
import axios from 'axios'
import { useTheme } from 'react-jss'
import { atom, useAtom } from 'jotai'
import { Close, Content, Overlay, Portal, Root } from '@radix-ui/react-dialog'
import {
    IkonPromptfejl,
    IkonPromptsucces,
    IkonUdraabstegn,
    Close as CloseIcon,
} from '../../../../assets/icons'
import { CaseDetail } from '../../../../models/CaseDetail'
import { useStyles } from '../../../ui/Dialog/Dialog'
import { Typography } from '../../../ui/Typography/Typography'
import { colors } from '../../../../theme/palette'
import { Button } from '../../../ui/Button/Button'
import { SimpleDialog } from '../../../ui/Dialog/SimpleDialog'
import { L, L_HTML } from '../../../providers/Lokalise'
import { closeCase, isCaseEmployerMailSent } from '../../../../api/salary'

import { profileAtom } from '../../../../api/profile/profile'
import { ErrorDisplay, openContactDialogAtom } from '../Contact/ContactDialog'
import { RestrictElementWithTooltip } from '../RestrictElementWithTooltip'

export type CloseCaseRowProps = {
    caseDetails: CaseDetail
    onUpdated?: () => void
}

export const caseDetailsUnsavedChangesAtom = atom<boolean>(false)

const CloseCaseRow: React.FC<CloseCaseRowProps> = ({
    caseDetails,
    onUpdated,
}) => {
    const [authProfile] = useAtom(profileAtom)
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.blue300
    const iconBorder = colors.white

    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>('')
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] =
        useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isClosed, setIsClosed] = useState<boolean>(false)
    const [showInitalErrorDialog, setShowInitalErrorDialog] =
        useState<boolean>(false)
    const [isCloseCaseDialogOpen, setIsCloseCaseDialogOpen] =
        useState<boolean>(false)
    const [caseDetailsUnsavedChanges] = useAtom(caseDetailsUnsavedChangesAtom)
    const [, setOpenContactDialog] = useAtom(openContactDialogAtom)
    const [isMailNotSentDialogOpen, setIsMailNotSentDialogOpen] =
        useState<boolean>(false)
    const [isCheckingMailStatus, setIsCheckingMailStatus] =
        useState<boolean>(false)
    const handleCloseCase = async (): Promise<void> => {
        // Validate reporting fields before submitting

        if (caseDetailsUnsavedChanges) {
            setIsUnsavedChangesDialogOpen(true)
            return
        }

        try {
            setIsLoading(true)
            const response = await closeCase({
                caseId: caseDetails.caseId,
                caseRegTime: caseDetails.caseRegistrationTime,
            })
            if (response.error) {
                // setErrorMessage(response.error.errors.Parameter[0])
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setOpenDialog(true)
                setIsLoading(false)
                setIsCloseCaseDialogOpen(false)
                return
            }
            setIsClosed(true)
            setIsLoading(false)
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 400) {
                    setErrorMessage(
                        <ErrorDisplay errors={error.response?.data?.errors} />
                    )
                    setOpenDialog(true)
                    setIsCloseCaseDialogOpen(false)
                } else {
                    console.error('Other error:', error.message)
                }
                setIsLoading(false)
            }
        }
    }

    const closeDialog = (): void => {
        setIsCloseCaseDialogOpen(false)
    }

    const handleConfirmCloseCase = (): void => {
        setIsCloseCaseDialogOpen(false)
        onUpdated?.()
        setIsClosed(false)
    }

    const renderCloseCaseDialog = (): JSX.Element => {
        return (
            <>
                <Typography
                    style={{ marginBottom: '30px' }}
                    size="L"
                    align="center"
                >
                    {/* Er du sikker på at du vil afslutte sagen? */}
                    {L('LØN_AFSLUT_SAG_DIALOG_TITEL_1')}
                </Typography>
                <Typography
                    tag="p"
                    size="S"
                    style={{ marginBottom: '30px' }}
                    align="center"
                >
                    {/* Når sagen er afsluttet, kan den ikke længere redigeres. */}
                    {L('LØN_AFSLUT_SAG_DIALOG_TEXT_1')}
                </Typography>
                <div
                    style={{
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'center',
                        marginTop: '1rem',
                    }}
                >
                    <Button
                        size="xlarge"
                        onClick={closeDialog}
                        type="secondary"
                    >
                        {L('LØN_AFSLUT_SAG_DIALOG_FORTRYD_KNAP')}
                    </Button>
                    <Button
                        size="xlarge"
                        onClick={handleCloseCase}
                        loading={isLoading}
                    >
                        {L('LØN_AFSLUT_SAG_DIALOG_AFSLUT_KNAP')}
                    </Button>
                </div>
            </>
        )
    }

    const renderClosedCaseDialog = (): JSX.Element => {
        return (
            <>
                <Typography
                    style={{ marginBottom: '30px' }}
                    size="L"
                    align="center"
                >
                    {/* Sagen er afsluttet */}
                    {L('LØN_AFSLUT_SAG_DIALOG_TITEL_2')}
                </Typography>
                <Typography
                    tag="p"
                    size="S"
                    style={{ marginBottom: '30px' }}
                    align="center"
                >
                    {/* <p
                        dangerouslySetInnerHTML={{
                            __html: `Sagen er sendt til din fagforening.<br /> Du kan nu sen den under
                    'Afsluttede sager'`,
                        }}
                    /> */}
                    {L_HTML('LØN_AFSLUT_SAG_DIALOG_TEXT_2')}
                </Typography>
                <div
                    style={{
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'center',
                        marginTop: '1rem',
                    }}
                >
                    <Button size="xlarge" onClick={handleConfirmCloseCase}>
                        {L('LØN_AFSLUT_SAG_DIALOG_OK_KNAP')}
                    </Button>
                </div>
            </>
        )
    }

    const validateCaseBeforeClose = (): boolean => {
        let dateRequired = false
        let fieldsMissing = false

        if (caseDetailsUnsavedChanges) {
            setIsUnsavedChangesDialogOpen(true)
            return false
        }

        // Validate reporting fields before submitting
        // newlyHiredStatus
        if (caseDetails.caseProcessing.newlyHiredStatus) {
            if (caseDetails.caseProcessing.newlyHiredStatus.statusMissing) {
                fieldsMissing = true
            }
            if (
                caseDetails.caseProcessing.newlyHiredStatus.dateRequired &&
                !caseDetails.caseProcessing.contactNewlyHiredDate
            ) {
                dateRequired = true
            }
        }

        // organizationStatus
        if (caseDetails.caseProcessing.organizationStatus) {
            if (caseDetails.caseProcessing.organizationStatus.statusMissing) {
                fieldsMissing = true
            }
            if (
                caseDetails.caseProcessing.organizationStatus.dateRequired &&
                !caseDetails.caseProcessing.contactNewlyHiredDate
            ) {
                dateRequired = true
            }
        }
        // salaryPlacementStatus
        if (caseDetails.caseProcessing.salaryPlacementStatus) {
            if (
                caseDetails.caseProcessing.salaryPlacementStatus.statusMissing
            ) {
                fieldsMissing = true
            }
            if (
                caseDetails.caseProcessing.salaryPlacementStatus.dateRequired &&
                !caseDetails.caseProcessing.contactNewlyHiredDate
            ) {
                dateRequired = true
            }
        }

        if (dateRequired || fieldsMissing) {
            setShowInitalErrorDialog(true)
            return false
        }

        return true
    }

    const handleCheckMailSentBeforeClose = async (): Promise<void> => {
        // First validate that the case can be closed
        if (!validateCaseBeforeClose()) {
            return
        }

        try {
            setIsCheckingMailStatus(true)
            const response = await isCaseEmployerMailSent(
                caseDetails.caseId,
                caseDetails.caseRegistrationTime
            )
            // If mail was sent (response is true), proceed with close case dialog
            if (response.harSendtEmailFraSag === true) {
                setIsCloseCaseDialogOpen(true)
            } else {
                // Show warning dialog that mail was not sent
                setIsMailNotSentDialogOpen(true)
            }
        } catch (error) {
            console.error('Error checking mail status:', error)
            // On error, proceed with closing the case dialog anyway
            setIsCloseCaseDialogOpen(true)
        } finally {
            setIsCheckingMailStatus(false)
        }
    }

    const handleCloseWithoutSending = async (): Promise<void> => {
        setIsMailNotSentDialogOpen(false)

        // Validation already passed before showing the mail not sent dialog
        try {
            setIsLoading(true)
            const response = await closeCase({
                caseId: caseDetails.caseId,
                caseRegTime: caseDetails.caseRegistrationTime,
            })
            if (response.error) {
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setOpenDialog(true)
                setIsLoading(false)
                return
            }
            // Set isClosed BEFORE opening dialog so it shows success view directly
            setIsClosed(true)
            setIsCloseCaseDialogOpen(true)
            setIsLoading(false)
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 400) {
                    setErrorMessage(
                        <ErrorDisplay errors={error.response?.data?.errors} />
                    )
                    setOpenDialog(true)
                } else {
                    console.error('Other error:', error.message)
                }
                setIsLoading(false)
            }
        }
    }

    const handleOpenContactDialog = (): void => {
        setIsMailNotSentDialogOpen(false)
        setOpenContactDialog(true)
    }

    const handleCloseMailNotSentDialog = (): void => {
        setIsMailNotSentDialogOpen(false)
    }

    const renderMailNotSentDialog = (): JSX.Element => {
        return (
            <>
                <Close
                    className={classes.button}
                    onClick={handleCloseMailNotSentDialog}
                >
                    <CloseIcon width="32px" />
                </Close>
                {/* <Button
                    size="xlarge"
                    onClick={handleCloseMailNotSentDialog}
                    type="ghost"
                >
                    {L('LØN_AFSLUT_SAG_MAIL_IKKE_SENDT_LUK_KNAP')}
                </Button> */}
                <Typography
                    style={{ marginBottom: '30px' }}
                    size="L"
                    align="center"
                >
                    {L('LØN_AFSLUT_SAG_MAIL_IKKE_SENDT_TITEL')}
                </Typography>
                <Typography
                    tag="p"
                    size="S"
                    style={{ marginBottom: '30px' }}
                    align="center"
                >
                    {L('LØN_AFSLUT_SAG_MAIL_IKKE_SENDT_TEKST')}
                </Typography>
                <div
                    style={{
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'center',
                        marginTop: '1rem',
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        size="xlarge"
                        onClick={handleCloseWithoutSending}
                        type="secondary"
                    >
                        {L('LØN_AFSLUT_SAG_MAIL_IKKE_SENDT_AFSLUT_KNAP')}
                    </Button>
                    <Button
                        size="xlarge"
                        onClick={handleOpenContactDialog}
                        type="primary"
                    >
                        {L('LØN_AFSLUT_SAG_MAIL_IKKE_SENDT_SEND_MAIL_KNAP')}
                    </Button>
                </div>
            </>
        )
    }

    return (
        <>
            {/* <div className={classes.column}> */}
            <div style={{ width: '100%' }}>
                <RestrictElementWithTooltip
                    restrictedAction={
                        !caseDetails?.allowedUpdateActionList?.includes(
                            'Afslut'
                        )
                    }
                >
                    <Button
                        size="xxlarge"
                        type="primary"
                        style={{ width: '100%' }}
                        onClick={handleCheckMailSentBeforeClose}
                        loading={isLoading || isCheckingMailStatus}
                        disabled={
                            isLoading ||
                            isCheckingMailStatus ||
                            !caseDetails?.allowedUpdateActionList?.includes(
                                'Afslut'
                            ) ||
                            authProfile.isImitator
                        }
                    >
                        {L('LØN_AFSLUT_SAG_KNAP')}
                    </Button>
                </RestrictElementWithTooltip>
            </div>
            {isCloseCaseDialogOpen && (
                <Root open={isCloseCaseDialogOpen}>
                    <Portal>
                        <Overlay className={classes.overlay} />
                        <Content className={`${classes.overlayContainer}`}>
                            <div className={`${classes.container} withIcon`}>
                                <div
                                    className={`${classes.circleIcon} withIcon`}
                                >
                                    {!isClosed ? (
                                        <IkonUdraabstegn
                                            width="46px"
                                            fill={colors.green500}
                                        />
                                    ) : (
                                        <IkonPromptsucces
                                            width="46px"
                                            fill={colors.green500}
                                        />
                                    )}
                                </div>
                                {isClosed
                                    ? renderClosedCaseDialog()
                                    : renderCloseCaseDialog()}
                            </div>
                        </Content>
                    </Portal>
                </Root>
            )}
            {openDialog && (
                <SimpleDialog
                    title="Fejl"
                    message={errorMessage}
                    closeDialog={() => setOpenDialog(false)}
                    isError
                    icon={<IkonPromptfejl width="46px" fill={colors.red500} />}
                />
            )}
            {showInitalErrorDialog && (
                <SimpleDialog
                    title={L('LØN_AFSLUT_SAG_VALIDERINGS_FEJL_TITEL')}
                    message={L('LØN_AFSLUT_SAG_VALIDERINGS_FEJL_TEKST')}
                    closeDialog={() => setShowInitalErrorDialog(false)}
                    icon={<IkonPromptfejl width="46px" fill={colors.red500} />}
                    isError
                />
            )}
            {isUnsavedChangesDialogOpen && (
                <SimpleDialog
                    title={L('LØN_AFSLUT_SAG_UGEMTE_ÆNDRINGER_TITEL')}
                    message={L('LØN_AFSLUT_SAG_UGEMTE_ÆNDRINGER_TEKST')}
                    buttonText={L('LØN_AFSLUT_SAG_UGEMTE_ÆNDRINGER_KNAP')}
                    closeDialog={() => setIsUnsavedChangesDialogOpen(false)}
                    isError
                    icon={<IkonPromptfejl width="46px" fill={colors.red500} />}
                />
            )}
            {isMailNotSentDialogOpen && (
                <Root open={isMailNotSentDialogOpen}>
                    <Portal>
                        <Overlay className={classes.overlay} />
                        <Content className={`${classes.overlayContainer}`}>
                            <div className={`${classes.container} withIcon`}>
                                <div
                                    className={`${classes.circleIcon} withIcon`}
                                >
                                    <IkonUdraabstegn
                                        width="46px"
                                        fill={colors.green500}
                                    />
                                </div>
                                {renderMailNotSentDialog()}
                            </div>
                        </Content>
                    </Portal>
                </Root>
            )}
        </>
    )
}

export { CloseCaseRow }
